import { makeObservable, observable } from 'mobx';
import {
  httpDelete,
  httpGet,
  httpPatch,
  httpPost,
  httpPut
} from 'src/axios/axiosUtils';
import { units } from 'src/axios/requests';
import { LoadingState } from 'src/axios/types';
import { ChecklistItemCategory, ViewChecklistItem } from 'src/models/unit';
import { Stores } from 'src/stores';
import { BaseBackendStore } from '../types';
import type {
  CategoryDto,
  GetBasicInformationDto,
  Reorder,
  SaveBasicInformationDto,
  UnitDetails
} from './types';

export class UnitStore extends BaseBackendStore {
  @observable unit: UnitDetails;

  @observable unitState: LoadingState = LoadingState.IDLE;

  constructor(public rootStore: Stores) {
    super();
    makeObservable(this);
  }

  async getUnit(unitId: number) {
    try {
      this.unitState = LoadingState.LOADING;
      this.unit = await httpGet(`${units}/${unitId}`, false, {
        headers: {
          'request-version': '2'
        }
      });
      this.unitState = LoadingState.DONE;
    } catch {
      this.unitState = LoadingState.FAILED;
    }
  }

  setUnit(unitToSet: UnitDetails) {
    this.unit = unitToSet;
  }

  /**
   *
   * @param unitId
   * @param payload
   * @description
   *  - create category
   *  - insert it locally at the beginning of the list
   *  - reorder current categories
   */
  async addNewCategory(unitId: number, payload: CategoryDto) {
    const createdCategory: ChecklistItemCategory = await httpPost(
      `${units}/${unitId}/categories`,
      payload
    );

    this.unit.checklistItemsCategories = [
      ...this.unit.checklistItemsCategories,
      {
        ...createdCategory,
        checklistItems: []
      }
    ];
  }

  async updateCategory(
    unitId: number,
    categoryId: number,
    payload: CategoryDto
  ) {
    await httpPut(`${units}/${unitId}/categories/${categoryId}`, payload);
    const index = this.unit.checklistItemsCategories.findIndex(
      (cat) => cat.id === categoryId
    );
    if (index > -1) {
      this.unit.checklistItemsCategories[index] = {
        ...this.unit.checklistItemsCategories[index],
        ...payload
      };
    }
  }

  async deleteCategory(unitId: number, categoryId: number) {
    await httpDelete(`${units}/${unitId}/categories/${categoryId}`);
    this.unit.checklistItemsCategories =
      this.unit.checklistItemsCategories.filter((cat) => cat.id !== categoryId);
  }

  async reorderCategories(unitId: number, categories: Reorder[]) {
    await httpPatch(`${units}/${unitId}/categories`, categories);
  }

  async deleteChecklistItem(unitId: string, categoryId: number, id: number) {
    await httpDelete(
      `${units}/${unitId}/categories/${categoryId}/checklist-items/${id}`
    );
    const index = this.unit.checklistItemsCategories.findIndex(
      (cat) => cat.id === categoryId
    );
    if (index > -1) {
      this.unit.checklistItemsCategories[index] = {
        ...this.unit.checklistItemsCategories[index],
        checklistItems: this.unit.checklistItemsCategories[
          index
        ].checklistItems.filter((item) => +item.id !== id)
      };
    }
  }

  async updateChecklistItem(
    unitId: string,
    categoryId: number,
    id: number,
    checklistItem: ViewChecklistItem
  ) {
    await httpPut(
      `${units}/${unitId}/categories/${categoryId}/checklist-items/${id}`,
      checklistItem
    );
    const categoryIndex = this.unit.checklistItemsCategories.findIndex(
      (cat) => cat.id === categoryId
    );
    if (categoryIndex < 0) {
      return;
    }
    const itemIndex = this.unit.checklistItemsCategories[
      categoryIndex
    ].checklistItems.findIndex((item) => +item.id === id);
    this.unit.checklistItemsCategories[categoryIndex].checklistItems[
      itemIndex
    ] = {
      ...checklistItem,
      id
    };
    this.unit.checklistItemsCategories[categoryIndex] = {
      ...this.unit.checklistItemsCategories[categoryIndex],
      checklistItems:
        this.unit.checklistItemsCategories[categoryIndex].checklistItems
    };
  }

  async addChecklistItem(
    unitId: string,
    categoryId: number,
    item: ViewChecklistItem
  ) {
    const { id }: ViewChecklistItem = await httpPost(
      `${units}/${unitId}/categories/${categoryId}/checklist-items`,
      item
    );
    const categoryIndex = this.unit.checklistItemsCategories.findIndex(
      (cat) => cat.id === categoryId
    );
    if (categoryIndex < 0) {
      return;
    }
    const orderedChecklistItems = [
      ...this.unit.checklistItemsCategories[categoryIndex].checklistItems,
      {
        id,
        ...item
      } as ViewChecklistItem
    ];
    this.unit.checklistItemsCategories[categoryIndex] = {
      ...this.unit.checklistItemsCategories[categoryIndex],
      checklistItems: orderedChecklistItems
    };
  }

  async reorderChecklistItems(
    unitId: string,
    categoryId: number,
    checklistItemsOrders: Reorder[]
  ) {
    await httpPatch(
      `${units}/${unitId}/categories/${categoryId}/checklist-items`,
      checklistItemsOrders
    );
  }

  async createUnit(unit: SaveBasicInformationDto) {
    await httpPost(`${units}`, unit);
  }

  updateUnitBasicInformation({
    client,
    description,
    imageURL,
    title,
    user,
  }: SaveBasicInformationDto) {
    this.unit = {
      ...this.unit,
      title,
      description,
      imageURL,
      client,
      user,
    };
  }

  get unitBasicInformationDto(): GetBasicInformationDto {
    return {
      client: {
        id: this.unit.client.id
      },
      description: this.unit.description,
      imageURL: this.unit.imageURL,
      title: this.unit.title,
      ...(this.unit.user && { user: {
        id: this.unit?.user?.id,
      }})
    };
  }

  resetUnit() {
    this.unit = undefined;
  }
}
